import React from 'react';
import { Page } from '../types';
import {
    ShieldCheckIcon, StarIcon, CheckCircleIcon, TargetIcon, CertificateIcon,
    ScaleIcon, DocumentTextIcon, CalendarDaysIcon, UsersIcon, SparklesIcon,
    BulletIcon, ExclamationTriangleIcon,
} from '../assets/icons';

const PORTAL_AFILIACAO = "https://painel.cttforcaeacao.com.br/portal";

interface ClubPageProps {
    onNavigate: (page: Page) => void;
}

/**
 * Landing do CLUBE DO CAÇADOR — CTT 1911.
 * Vende: afiliação ao Clube de Tiro 1911 + serviços de despachante CAC +
 * o diferencial de caça legal na fazenda parceira (CAR) e o Dia de Caça do Javaporco.
 * Preços oficiais: afiliação R$100/mês ou R$960/ano (-20%).
 */
const ClubPage: React.FC<ClubPageProps> = ({ onNavigate }) => {
    return (
        <div className="bg-gray-900 min-h-screen text-white font-sans selection:bg-arsenal-red selection:text-white pb-24">

            {/* ───────────────────────── HERO ───────────────────────── */}
            <section className="relative py-32 overflow-hidden bg-gray-900">
                {/* arte branded do Clube do Caçador (SVG vetorial — identidade CTT) */}
                <div className="absolute inset-0 bg-[url('/clube-cacador-hero.svg')] bg-cover bg-center opacity-95"></div>
                <div className="absolute inset-0 bg-gradient-to-b from-gray-900/80 via-gray-900/45 to-gray-900"></div>

                <div className="relative z-10 container mx-auto px-4 text-center animate-fade-in">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-arsenal-red/10 border border-arsenal-red/30 rounded-full mb-6">
                        <TargetIcon className="w-5 h-5 text-arsenal-red" />
                        <span className="text-arsenal-red font-bold uppercase tracking-widest text-sm">Clube de Tiro 1911 · Uberaba/MG</span>
                    </div>
                    <h1 className="text-5xl md:text-7xl font-black text-white tracking-tighter mb-6">
                        CLUBE DO <span className="text-transparent bg-clip-text bg-gradient-to-r from-arsenal-red to-red-600">CAÇADOR</span>
                    </h1>
                    <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                        Associe-se, fique <span className="text-white font-semibold">100% em dia com o Exército e a PF</span> — e tenha
                        onde caçar dentro da lei. A papelada é com a gente; a caça é com você.
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
                        <a
                            href={PORTAL_AFILIACAO}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-8 py-4 rounded-xl bg-arsenal-red text-white font-bold uppercase tracking-wide hover:bg-red-700 shadow-lg shadow-red-900/30 hover:-translate-y-1 transition-all duration-300 text-center"
                        >
                            Quero me associar
                        </a>
                        <button
                            onClick={() => onNavigate('cac-services')}
                            className="px-8 py-4 rounded-xl bg-white/10 text-white font-bold uppercase tracking-wide hover:bg-white/20 hover:-translate-y-1 transition-all duration-300"
                        >
                            Ver serviços do despachante
                        </button>
                    </div>
                </div>
            </section>

            {/* ──────────────── FAIXA DE PROMESSA ──────────────── */}
            <section className="container mx-auto px-4 -mt-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto">
                    {[
                        { icon: <ShieldCheckIcon className="w-7 h-7" />, t: 'Sempre regular', d: 'CR, CRAF e laudos monitorados e renovados antes de vencer.' },
                        { icon: <ScaleIcon className="w-7 h-7" />, t: 'Suporte jurídico', d: 'Assessoria CAC e jurídica quando você precisar.' },
                        { icon: <TargetIcon className="w-7 h-7" />, t: 'Onde caçar', d: 'Acesso a área legalizada para caça de controle (fazenda parceira).' },
                    ].map((b, i) => (
                        <div key={i} className="flex items-start gap-4 p-6 rounded-2xl bg-gray-800/40 border border-gray-800">
                            <div className="text-arsenal-red flex-shrink-0">{b.icon}</div>
                            <div>
                                <h3 className="font-bold text-white">{b.t}</h3>
                                <p className="text-sm text-gray-400 mt-1 leading-relaxed">{b.d}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ──────────────── SERVIÇOS DO DESPACHANTE ──────────────── */}
            <section className="container mx-auto px-4 mt-24">
                <div className="text-center max-w-2xl mx-auto mb-12">
                    <span className="text-arsenal-red font-bold uppercase tracking-widest text-sm">Despachante CAC</span>
                    <h2 className="text-4xl font-black text-white tracking-tight mt-2">Sua papelada, resolvida</h2>
                    <p className="text-gray-400 mt-4">Cuidamos de todo o processo junto ao Exército e à Polícia Federal. Você atira e caça; a burocracia fica com a gente.</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                    {[
                        { icon: <CertificateIcon className="w-8 h-8" />, t: 'Registro CAC (CR)', d: 'Abertura e renovação do Certificado de Registro de colecionador, atirador e caçador.' },
                        { icon: <DocumentTextIcon className="w-8 h-8" />, t: 'Autorização de compra', d: 'Aquisição de armas e munições com toda a documentação no padrão do Exército.' },
                        { icon: <BulletIcon className="w-8 h-8" />, t: 'CRAF e acervo', d: 'Registro e transferência de armas no SIGMA, mantendo seu acervo regular.' },
                        { icon: <TargetIcon className="w-8 h-8" />, t: 'Registro de Caçador', d: 'Habilitação como caçador e o CTF/SIMAF para a caça de controle de javali.' },
                        { icon: <CalendarDaysIcon className="w-8 h-8" />, t: 'Renovações e laudos', d: 'Laudo psicológico, laudo de tiro e habitualidade — sem deixar vencer.' },
                        { icon: <ShieldCheckIcon className="w-8 h-8" />, t: 'Regularização', d: 'Está com algo vencido ou pendente? A gente põe sua situação em dia.' },
                    ].map((s, i) => (
                        <div key={i} className="group p-7 rounded-2xl bg-gray-900/50 border border-gray-800 hover:border-arsenal-red/50 hover:bg-gray-800/50 transition-all duration-300">
                            <div className="text-arsenal-red mb-4 group-hover:scale-110 transition-transform duration-300">{s.icon}</div>
                            <h3 className="font-bold text-white text-lg">{s.t}</h3>
                            <p className="text-sm text-gray-400 mt-2 leading-relaxed">{s.d}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ──────────────── AFILIAÇÃO (PREÇOS REAIS) ──────────────── */}
            <section className="container mx-auto px-4 mt-28">
                <div className="text-center max-w-2xl mx-auto mb-12">
                    <span className="text-arsenal-red font-bold uppercase tracking-widest text-sm">Associe-se</span>
                    <h2 className="text-4xl font-black text-white tracking-tight mt-2">Afiliação ao Clube de Tiro 1911</h2>
                    <p className="text-gray-400 mt-4">Uma associação, todos os benefícios. Atinja a habitualidade, mantenha-se regular e desbloqueie a caça na fazenda parceira.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                    <PlanoCard
                        titulo="Mensal"
                        preco="R$ 100"
                        periodo="/mês"
                        descricao="Flexível, cancele quando quiser."
                        beneficios={[
                            'Filiação ativa ao Clube 1911',
                            'Habitualidade e uso do estande',
                            'Acesso ao Clube do Caçador',
                            'Desconto nos serviços de despachante',
                            'Convite para o Dia de Caça do Javaporco',
                        ]}
                        cta="Assinar mensal"
                    />
                    <PlanoCard
                        titulo="Anual"
                        preco="R$ 960"
                        periodo="/ano"
                        selo="20% OFF"
                        destaque
                        descricao="Equivale a R$ 80/mês · economize R$ 240 no ano."
                        beneficios={[
                            'Tudo do plano mensal',
                            '20% de desconto (R$ 240/ano)',
                            'Prioridade na agenda do despachante',
                            'Prioridade no Dia de Caça',
                            'Selo de membro fundador',
                        ]}
                        cta="Assinar anual e economizar"
                    />
                </div>
            </section>

            {/* ──────────────── DIFERENCIAL: CAÇA NA FAZENDA PARCEIRA ──────────────── */}
            <section className="container mx-auto px-4 mt-28">
                <div className="relative max-w-6xl mx-auto rounded-3xl overflow-hidden border border-arsenal-red/30">
                    <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1500534623283-312aade485b7?q=80&w=2070&auto=format&fit=crop')] bg-cover bg-center opacity-20"></div>
                    <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-gray-900/90 to-gray-900/60"></div>
                    <div className="relative z-10 p-10 md:p-14 grid md:grid-cols-2 gap-10 items-center">
                        <div>
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-arsenal-red/20 border border-arsenal-red/40 rounded-full mb-5">
                                <SparklesIcon className="w-4 h-4 text-arsenal-red" />
                                <span className="text-arsenal-red font-bold uppercase tracking-widest text-xs">Novo benefício · em implantação</span>
                            </div>
                            <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight">Cace dentro da lei na fazenda parceira</h2>
                            <p className="text-gray-300 mt-4 leading-relaxed">
                                Fechamos parceria com uma propriedade rural com <span className="text-white font-semibold">CAR (Cadastro Ambiental Rural)</span> e
                                autorização de manejo. O caçador que <span className="text-white font-semibold">não tem área própria ou parceria</span> passa
                                a ter um <span className="text-white font-semibold">local legalizado para a prática da caça de controle</span> — como benefício
                                exclusivo de quem se associa pelo CTT 1911.
                            </p>
                        </div>
                        <ul className="space-y-4">
                            {[
                                'Área rural com CAR e autorização ambiental',
                                'Acesso liberado para associados em dia',
                                'Acompanhamento da documentação (CTF/SIMAF)',
                                'Segurança jurídica: caça de controle, conforme a lei',
                            ].map((f, i) => (
                                <li key={i} className="flex items-start gap-3 text-gray-200">
                                    <CheckCircleIcon className="w-6 h-6 text-arsenal-red flex-shrink-0" />
                                    <span>{f}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* ──────────────── DIA DE CAÇA DO JAVAPORCO ──────────────── */}
            <section className="container mx-auto px-4 mt-24">
                <div className="max-w-5xl mx-auto text-center">
                    <span className="text-arsenal-red font-bold uppercase tracking-widest text-sm">Evento dos associados</span>
                    <h2 className="text-4xl font-black text-white tracking-tight mt-2">Dia de Caça do Javaporco 🐗</h2>
                    <p className="text-gray-400 mt-4 max-w-3xl mx-auto leading-relaxed">
                        O <span className="text-white font-semibold">javali (javaporco)</span> é espécie exótica invasora — seu controle por caça é
                        <span className="text-white font-semibold"> autorizado pela legislação ambiental</span> (IN IBAMA nº 03/2013), mediante cadastro
                        no CTF e manejo via SIMAF. Vamos organizar, na fazenda parceira, dias de caça de controle para os membros do Clube — com toda a
                        documentação em ordem.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto mt-12">
                    {[
                        { icon: <UsersIcon className="w-7 h-7" />, t: 'Só para associados', d: 'Vagas exclusivas para membros do Clube de Tiro 1911 em dia.' },
                        { icon: <DocumentTextIcon className="w-7 h-7" />, t: 'Tudo regularizado', d: 'CTF, SIMAF e autorização de manejo conferidos pela nossa equipe.' },
                        { icon: <TargetIcon className="w-7 h-7" />, t: 'Manejo responsável', d: 'Controle do javali com segurança, ética e dentro da lei.' },
                    ].map((c, i) => (
                        <div key={i} className="p-7 rounded-2xl bg-gray-800/40 border border-gray-800 text-center">
                            <div className="text-arsenal-red inline-flex mb-4">{c.icon}</div>
                            <h3 className="font-bold text-white">{c.t}</h3>
                            <p className="text-sm text-gray-400 mt-2 leading-relaxed">{c.d}</p>
                        </div>
                    ))}
                </div>

                <div className="max-w-3xl mx-auto mt-8 flex items-start gap-3 p-4 rounded-xl bg-amber-500/5 border border-amber-500/20">
                    <ExclamationTriangleIcon className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-200/80 leading-relaxed">
                        Caça permitida é exclusivamente a de <strong>controle de espécie exótica invasora (javali)</strong>, conforme a legislação
                        ambiental vigente e mediante registros (CR, CTF/IBAMA, SIMAF). Não promovemos caça de espécies nativas. Parceria de área em fase de formalização.
                    </p>
                </div>
            </section>

            {/* ──────────────── COMO FUNCIONA ──────────────── */}
            <section className="container mx-auto px-4 mt-28">
                <div className="text-center mb-12">
                    <h2 className="text-4xl font-black text-white tracking-tight">Como funciona</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                    {[
                        { n: '01', t: 'Associe-se', d: 'Escolha mensal ou anual e ative sua filiação ao Clube 1911.' },
                        { n: '02', t: 'Regularize', d: 'Nosso despachante coloca seu CR, CRAF e laudos 100% em dia.' },
                        { n: '03', t: 'Cace legal', d: 'Acesse a fazenda parceira e participe do Dia de Caça do Javaporco.' },
                    ].map((p, i) => (
                        <div key={i} className="relative p-8 rounded-2xl bg-gray-900/50 border border-gray-800">
                            <span className="text-6xl font-black text-gray-800 absolute top-4 right-6 select-none">{p.n}</span>
                            <h3 className="text-xl font-bold text-white relative z-10">{p.t}</h3>
                            <p className="text-gray-400 mt-3 relative z-10 leading-relaxed">{p.d}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ──────────────── CTA FINAL ──────────────── */}
            <section className="container mx-auto px-4 mt-28">
                <div className="max-w-4xl mx-auto text-center p-12 rounded-3xl bg-gradient-to-br from-arsenal-red/20 to-gray-900 border border-arsenal-red/30">
                    <StarIcon className="w-12 h-12 text-arsenal-red mx-auto mb-6" />
                    <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight">Entre para o Clube do Caçador</h2>
                    <p className="text-gray-300 mt-4 max-w-xl mx-auto">Regularização, habitualidade e caça legal num só lugar. Vagas para o Dia de Caça são limitadas aos associados.</p>
                    <a
                        href={PORTAL_AFILIACAO}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-8 inline-block px-10 py-4 rounded-xl bg-arsenal-red text-white font-bold uppercase tracking-wide hover:bg-red-700 shadow-lg shadow-red-900/30 hover:-translate-y-1 transition-all duration-300"
                    >
                        Quero me associar agora
                    </a>
                </div>
            </section>
        </div>
    );
};

interface PlanoCardProps {
    titulo: string;
    preco: string;
    periodo: string;
    descricao: string;
    beneficios: string[];
    cta: string;
    selo?: string;
    destaque?: boolean;
}

const PlanoCard: React.FC<PlanoCardProps> = ({ titulo, preco, periodo, descricao, beneficios, cta, selo, destaque }) => (
    <div className={`relative flex flex-col p-8 rounded-2xl border ${destaque ? 'border-arsenal-red bg-gray-800/80 shadow-[0_0_50px_-15px_rgba(230,57,70,0.4)]' : 'border-gray-800 bg-gray-900/40 hover:border-gray-700'} transition-all duration-300`}>
        {selo && (
            <div className="absolute top-0 right-8 -translate-y-1/2 bg-arsenal-red text-white text-xs font-bold uppercase py-1 px-3 rounded-full shadow-lg tracking-wider">
                {selo}
            </div>
        )}
        <div className="mb-8">
            <h3 className="text-lg font-bold text-gray-400 uppercase tracking-widest mb-2">{titulo}</h3>
            <div className="flex items-baseline gap-1">
                <span className="text-5xl font-black text-white tracking-tight">{preco}</span>
                <span className="text-gray-500 font-medium">{periodo}</span>
            </div>
            <p className="text-gray-400 mt-4 text-sm leading-relaxed">{descricao}</p>
        </div>
        <ul className="flex-1 space-y-4 mb-8">
            {beneficios.map((b, idx) => (
                <li key={idx} className="flex items-start gap-3 text-sm text-gray-300">
                    <CheckCircleIcon className={`w-5 h-5 flex-shrink-0 ${destaque ? 'text-arsenal-red' : 'text-gray-600'}`} />
                    <span>{b}</span>
                </li>
            ))}
        </ul>
        <a
            href={PORTAL_AFILIACAO}
            target="_blank"
            rel="noopener noreferrer"
            className={`w-full py-4 rounded-xl font-bold uppercase tracking-wide transition-all duration-300 text-center block ${destaque ? 'bg-arsenal-red text-white hover:bg-red-700 shadow-lg shadow-red-900/20 hover:-translate-y-1' : 'bg-white/10 text-white hover:bg-white/20 hover:-translate-y-1'}`}
        >
            {cta}
        </a>
    </div>
);

export default ClubPage;
